import { HighlightAlt } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import RefreshIcon from "../../../assets/Fibo/icons/RefreshIcon";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import ConfigSwitch from "../../../components/common/BriaSwitch/Config/ConfigSwitch";
import BriaTooltip from "../../../components/common/BriaTooltip/BriaTooltip";
import { POSTHOG_EVENTS } from "../../../constants/PostHogEvents";
import { usePostHog } from "../../../hooks/usePostHog";
import { useAppStore } from "../../../hooks/useStores";
import styles from "../../../layout/LabsLayout/Results/LabResults.module.scss";
import { TextToImageConfigType } from "../../../models/text-to-image";
import { isFibo2App, isFiboBbqApp } from "../../../utils";

type FiboResultInfoProps = {
	config: TextToImageConfigType;
	mode: string;
	modeLabel?: string;
	refImageUrl?: string;
	onReferenceImageClick: (imageUrl: string) => void;
	resultId: string;
};

const FiboResultInfo = ({
	config,
	mode,
	modeLabel,
	refImageUrl,
	onReferenceImageClick,
	resultId,
}: FiboResultInfoProps) => {
	const { t } = useTranslation("fibo", { keyPrefix: "lab.settings.fiboModels" });
	const { t: tCanvas } = useTranslation("fibo", { keyPrefix: "lab.canvas" });
	const { trackEvent } = usePostHog();
	const { fiboStore } = useAppStore();
	const { structuredPromptStore } = fiboStore;
	const formattedVersion = config?.model_version
		? config.model_version.charAt(0).toUpperCase() + config.model_version.slice(1).toLowerCase()
		: isFiboBbqApp()
			? "BBQ"
			: isFibo2App()
				? "2"
				: "";

	const { playgroundStore } = useAppStore();
	const group = playgroundStore.labResultGroups.find((g) => g.id === resultId);
	const isRefineTab = fiboStore.activeTab === "refine";
	// The canvas tools cluster (draw an embed area / show all objects) shares one gate:
	// a refine-tab FIBO 2 or BBQ result that actually has a vgl to draw against.
	const canUseCanvasTools =
		isRefineTab &&
		(isFiboBbqApp() || isFibo2App()) &&
		playgroundStore.isFiboResultGroup(group) &&
		!!group.structured_prompt;
	const isEmbedDrawActive = structuredPromptStore.isEmbedDrawActive;

	const handleEmbedImageClick = () => {
		if (isEmbedDrawActive) {
			structuredPromptStore.clearEmbedDraw();
			trackEvent(POSTHOG_EVENTS.FIBO_EMBED_DRAW_CANCELLED);
			return;
		}
		structuredPromptStore.startEmbedDraw();
		trackEvent(POSTHOG_EVENTS.FIBO_EMBED_DRAW_STARTED);
	};

	return (
		<Box className={styles.extraResultInfo}>
			<Typography className={styles.extraResultInfoText}>
				{t("modelLabel", { version: formattedVersion })}
			</Typography>
			<Typography className={styles.extraResultInfoText}>
				{modeLabel}
				{mode}
			</Typography>
			{refImageUrl && (
				<Box className={styles.referenceImageContainer} onClick={() => onReferenceImageClick(refImageUrl)}>
					<Box className={styles.refreshIconContainer}>
						<RefreshIcon />
					</Box>
					<img src={refImageUrl} alt="reference" className={styles.referenceImage} />
				</Box>
			)}
			{canUseCanvasTools && (
				<Box className={styles.canvasTools}>
					<BriaTooltip title={tCanvas("embedImageTooltip")}>
						<BriaButton
							buttonType="textMedium"
							startIcon={<HighlightAlt sx={{ fontSize: 16 }} />}
							className={clsx(styles.embedImageButton, {
								[styles.embedImageButtonActive]: isEmbedDrawActive,
							})}
							onClick={handleEmbedImageClick}
						>
							{tCanvas("embedImage")}
						</BriaButton>
					</BriaTooltip>
					<ConfigSwitch
						label={t("showAllObjects")}
						checked={structuredPromptStore.showAllObjects}
						onChange={(checked) => structuredPromptStore.setShowAllObjects(checked)}
						className={styles.showAllObjectsSwitch}
					/>
				</Box>
			)}
		</Box>
	);
};

export default observer(FiboResultInfo);
